import { DomainError, TransportError } from "@econ-v1/domain";
/**
 * Surfaced when a `node/client-rpc/1` response is a `402 PaymentRequired`
 * L402 payment challenge — the WebTransport/WebRTC-carrier equivalent of
 * the HTTP carrier's 402 handling. Deliberately NOT a `TransportError`
 * (ticket #1866): `ManagedHomeNodeClient.invoke()`/`.deltas()` only retire
 * the active transport and fail over to another rung for a `TransportError`
 * (`isPathFailure`), and a payment challenge is neither a transport failure
 * nor an authorization/request failure the caller should treat as
 * terminal — it is the same "pay this invoice, then retry" signal, carried
 * over `node/client-rpc/1` instead of an HTTP `WWW-Authenticate` header.
 * Callers drive the existing L402 payment flow off `macaroon`/`invoice`
 * exactly as they would off the HTTP carrier's challenge, then retry the
 * request.
 *
 * Never log `macaroon` or `invoice` (Constitution Principle VIII) — they
 * are credential/payment-secret material, not diagnostic detail. This is
 * enforced structurally, not just by convention: `macaroon`/`invoice` are
 * defined as non-enumerable own properties (mirroring the `RedactedHeaders`
 * `Debug` redaction the Rust side applies to `ClientRpcRequest`/
 * `ClientRpcSetup` headers in `core/adapters-network/src/client_rpc/frame.rs`),
 * and `toJSON()` below emits only non-secret fields with both replaced by a
 * redaction marker — so `JSON.stringify(err)` (the shape the transport
 * supervisor's telemetry path serializes errors through) can never emit
 * either verbatim, while direct property access (`err.macaroon`/
 * `err.invoice`) still works for the payment flow that needs the real
 * values.
 */
export class L402PaymentChallengeError extends Error {
    amountSats;
    description;
    constructor(details) {
        super(details.message !== undefined && details.message.length > 0 ? details.message : "Lightning payment required");
        this.name = "L402PaymentChallengeError";
        this.amountSats = details.amountSats;
        this.description = details.description;
        Object.defineProperty(this, "invoice", {
            value: details.invoice,
            enumerable: false,
            writable: false,
            configurable: false,
        });
        Object.defineProperty(this, "macaroon", {
            value: details.macaroon,
            enumerable: false,
            writable: false,
            configurable: false,
        });
    }
    /**
     * The redacting `JSON.stringify` consults instead of the default
     * enumerable-own-property walk — never emits `macaroon`/`invoice`.
     */
    toJSON() {
        return {
            name: this.name,
            message: this.message,
            amountSats: this.amountSats,
            description: this.description,
            macaroon: "<redacted>",
            invoice: "<redacted>",
        };
    }
}
export function isL402PaymentChallengeError(error) {
    return error instanceof L402PaymentChallengeError;
}
/**
 * Surfaced by `ManagedHomeNodeClient` (ticket #1866) when an
 * `L402PaymentHandler` supplied to it rejects while paying a challenge
 * extracted from a `402` `node/client-rpc/1` response. Deliberately NOT a
 * `TransportError` — a payment failure (insufficient balance, a declined
 * Lightning payment, an operator declining a prompt, …) is neither a
 * transport failure nor something the retry loop can recover from by
 * itself, so it must surface to the caller cleanly rather than trigger a
 * rung failover or a retry loop.
 */
export class L402PaymentFailedError extends Error {
    constructor(cause) {
        super("L402 payment failed");
        this.name = "L402PaymentFailedError";
        this.cause = cause;
    }
}
/**
 * Surfaced by `ManagedHomeNodeClient` (ticket #1866) when the request
 * retried with a paid `x-l402` token is answered with ANOTHER `402` —
 * i.e. the home node did not accept the payment (wrong preimage, macaroon
 * caveat mismatch, invoice already spent by a concurrent request, …).
 * `ManagedHomeNodeClient` retries a challenge at most once per original
 * request; a second challenge is always terminal, never looped.
 */
export class L402PaymentRetryDeniedError extends Error {
    constructor() {
        super("Home node rejected the retried request after payment (repeated L402 payment challenge)");
        this.name = "L402PaymentRetryDeniedError";
    }
}
/**
 * Maps one `node/client-rpc/1` response's status/error onto the same
 * `DomainError`/`TransportError` split the HTTP carrier's envelope mapping
 * already makes (`mapEnvelopeError` in `../http-home-node-client.ts`):
 * `bad_request`/`scope_denied`/`route_not_found` (and the equivalent bare
 * 400/401/403/404/409 status codes a future dispatcher revision might send
 * without an `error.code`) are `DomainError`s, never `TransportError`s. A
 * `402` is a third, distinct case — an `L402PaymentChallengeError` (see
 * above), also never a `TransportError`.
 *
 * This split is load-bearing for ticket #1826's "domain/authorization
 * errors never trigger network failover" invariant (and #1866's identical
 * invariant for payment challenges): `ManagedHomeNodeClient` only retires
 * the active transport and falls back to another rung for a
 * `TransportError` (`isPathFailure`), never a `DomainError` or an
 * `L402PaymentChallengeError`. Before this helper existed,
 * `WebTransportHomeNodeClient` and `WebRtcHomeNodeClient` each mapped every
 * 4xx client-rpc response — including a plain scope denial — to a
 * `TransportError`, which meant an authorization failure on an
 * otherwise-healthy LAN WebTransport or WAN WebRTC session incorrectly tore
 * the whole transport down and failed over. Shared by every non-HTTP
 * client-rpc carrier so the mapping can never drift between them again.
 *
 * `body` is only consulted for a `402` (to extract the L402 challenge); it
 * is ignored for every other status. Carriers pass the response's raw,
 * still-`unknown` body straight through — this function is the only place
 * that inspects its shape.
 */
export function clientRpcResponseError(status, error, fallbackMessage, body) {
    const message = error?.message ?? fallbackMessage;
    switch (error?.code) {
        case "bad_request":
            return new DomainError({ code: "invalid_request", message });
        case "scope_denied":
            return new DomainError({ code: "unauthorized", message });
        case "route_not_found":
            return new DomainError({ code: "not_found", message });
        default:
            break;
    }
    if (status === 402)
        return l402PaymentChallengeError(body, fallbackMessage);
    if (status === 400)
        return new DomainError({ code: "invalid_request", message });
    if (status === 401 || status === 403)
        return new DomainError({ code: "unauthorized", message });
    if (status === 404)
        return new DomainError({ code: "not_found", message });
    if (status === 409)
        return new DomainError({ code: "conflict", message });
    return new TransportError({ code: "unavailable", message });
}
function l402PaymentChallengeError(body, fallbackMessage) {
    const challenge = parseL402PaymentChallengeBody(body);
    if (challenge === undefined) {
        return new DomainError({
            code: "invalid_request",
            message: "client-rpc 402 response was missing the L402 payment challenge",
        });
    }
    return new L402PaymentChallengeError({ ...challenge, message: challenge.description || fallbackMessage });
}
function parseL402PaymentChallengeBody(body) {
    if (typeof body !== "object" || body === null)
        return undefined;
    const record = body;
    const { macaroon, invoice, amount_sats: amountSats, description } = record;
    if (typeof macaroon !== "string" || macaroon.length === 0)
        return undefined;
    if (typeof invoice !== "string" || invoice.length === 0)
        return undefined;
    if (typeof amountSats !== "number" || !Number.isFinite(amountSats))
        return undefined;
    if (typeof description !== "string")
        return undefined;
    return { amountSats, description, invoice, macaroon };
}
//# sourceMappingURL=response-error.js.map